// Pagination state for the merged Inventory panel's "All materials" list.
// Default page size 10; user-selectable from 5 / 10 / 20 / 50 per the
// Receive+Inventory merge spec. Reset-to-page-0 effect fires whenever
// the underlying filtered list shrinks below the current offset (e.g.
// the user typed a search term that knocks the list down to 3 items
// while we were sitting on page 4 of the unfiltered list).
import { useEffect, useState } from "react";

export const ALL_MATERIALS_PAGE_SIZES = [5, 10, 20, 50] as const;
export type AllMaterialsPageSize = (typeof ALL_MATERIALS_PAGE_SIZES)[number];

const DEFAULT_PAGE_SIZE: AllMaterialsPageSize = 10;

export type AllMaterialsPagination<T> = {
  page: number;
  pageSize: AllMaterialsPageSize;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  pageItems: T[];
  setPage: (next: number) => void;
  setPageSize: (next: AllMaterialsPageSize) => void;
};

export function useInventoryAllMaterialsPagination<T>(
  items: readonly T[]
): AllMaterialsPagination<T> {
  const [pageSize, setPageSizeState] = useState<AllMaterialsPageSize>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  // Snap back to page 0 whenever the underlying list shrinks past the
  // current offset (filter typed, items removed, etc.). Cheaper than
  // reaching out to filter callbacks and matches the "stay visible"
  // intent.
  useEffect(() => {
    if (page > 0 && page * pageSize >= items.length) setPage(0);
  }, [items.length, page, pageSize]);

  // Changing page size keeps the user's first-visible item roughly in
  // view rather than yanking them to a random offset; simplest impl is
  // resetting to page 0 (matches the AdsAssistant pagination posture).
  function setPageSize(next: AllMaterialsPageSize) {
    setPageSizeState(next);
    setPage(0);
  }

  const pageCount = items.length > 0 ? Math.ceil(items.length / pageSize) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const rangeStart = items.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, items.length);
  const pageItems = items.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return {
    page: safePage,
    pageSize,
    pageCount,
    rangeStart,
    rangeEnd,
    pageItems,
    setPage,
    setPageSize,
  };
}
